import { useEffect, useState } from "react";

import { DosespotMedication, WorkflowType } from "@/api/generated/models";
import { useSendNoriMessage } from "@/api/mutations/conversation";
import { useAnalytics } from "@/hooks/analytics";
import { PrescriptionAnalysis } from "./PrescriptionAnalysis";
import { PrescriptionNoMatch } from "./PrescriptionNoMatch";
import { PrescriptionResult } from "./PrescriptionResult";
import { PrescriptionScan } from "./PrescriptionScan";
import { PrescriptionUnreadable } from "./PrescriptionUnreadable";
import {
  type PrescriptionAnalysisResult,
  PrescriptionAnalysisStatus,
  usePrescriptionAnalysis,
} from "./usePrescriptionAnalysis";

enum PrescriptionScanStep {
  Scan = "scan",
  Analyzing = "analyzing",
  Result = "result",
  Unreadable = "unreadable",
  NoMatch = "no_match",
}

type FlowStep =
  | { step: PrescriptionScanStep.Scan }
  | { step: PrescriptionScanStep.Analyzing; imageUri: string }
  | {
      step: PrescriptionScanStep.Result;
      imageUri: string;
      data: Extract<PrescriptionAnalysisResult, { status: "success" }>;
    }
  | {
      step: PrescriptionScanStep.Unreadable;
      imageUri: string;
      medications: DosespotMedication[];
    }
  | { step: PrescriptionScanStep.NoMatch; error?: string };

interface PrescriptionScanFlowProps {
  onClose: () => void;
}

export const PrescriptionScanFlow = ({
  onClose,
}: PrescriptionScanFlowProps) => {
  const [flowState, setFlowState] = useState<FlowStep>({
    step: PrescriptionScanStep.Scan,
  });
  const { analyze, resetUpload, result } = usePrescriptionAnalysis();
  const { sendNoriMessage, isPending } = useSendNoriMessage();
  const { track } = useAnalytics();

  useEffect(() => {
    track("superclinical-__entered-prescription-scan-flow");
  }, [track]);

  useEffect(() => {
    if (!result || flowState.step !== PrescriptionScanStep.Analyzing) return;
    const { imageUri } = flowState;
    if (result.status === PrescriptionAnalysisStatus.Success) {
      setFlowState({
        step: PrescriptionScanStep.Result,
        imageUri,
        data: result,
      });
    } else if (result.status === PrescriptionAnalysisStatus.Unreadable) {
      setFlowState({
        step: PrescriptionScanStep.Unreadable,
        imageUri,
        medications: result.medications,
      });
    } else if (result.status === PrescriptionAnalysisStatus.NoMatch) {
      setFlowState({ step: PrescriptionScanStep.NoMatch, error: result.error });
    }
  }, [result, flowState]);

  const handleMedicationSelected = (medication: DosespotMedication) => {
    sendNoriMessage([medication], {
      type: "rx_renewal_selected_medication",
      agent: "prescription_renewal_consent_agent",
      workflowType: WorkflowType.rx_renewal,
      onConversationCreation: onClose,
    });
  };

  const handlePhotoCaptured = (imageUri: string) => {
    setFlowState({ step: PrescriptionScanStep.Analyzing, imageUri });
    analyze(imageUri);
  };

  switch (flowState.step) {
    case PrescriptionScanStep.Scan:
      return <PrescriptionScan onPhotoCaptured={handlePhotoCaptured} />;
    case PrescriptionScanStep.Analyzing:
      return <PrescriptionAnalysis />;
    case PrescriptionScanStep.Result:
      return (
        <PrescriptionResult
          imageUri={flowState.imageUri}
          result={flowState.data}
          onContinue={() => handleMedicationSelected(flowState.data.medication)}
          isContinuePending={isPending}
          onTryAgain={() => {
            resetUpload();
            setFlowState({ step: PrescriptionScanStep.Scan });
          }}
        />
      );
    case PrescriptionScanStep.Unreadable:
      return (
        <PrescriptionUnreadable
          imageUri={flowState.imageUri}
          medications={flowState.medications}
          onContinue={handleMedicationSelected}
          isContinuePending={isPending}
          onTryAgain={() => {
            resetUpload();
            setFlowState({ step: PrescriptionScanStep.Scan });
          }}
        />
      );
    case PrescriptionScanStep.NoMatch:
      return (
        <PrescriptionNoMatch
          onClose={onClose}
          error={flowState.error}
          onTryAgain={() => {
            resetUpload();
            setFlowState({ step: PrescriptionScanStep.Scan });
          }}
        />
      );
  }
};
